import { Component, OnInit, ViewChild } from '@angular/core';
import { DesktopSidebarComponent } from "../../../../shared/components/desktop-sidebar/desktop-sidebar.component";
import { DesktopTopbarComponent } from "../../../../shared/components/desktop-topbar/desktop-topbar.component";
import { MobileTopbarComponent } from "../../../../shared/components/mobile-topbar/mobile-topbar.component";
import { TabButtonComponent } from "../../../../shared/components/tab-button/tab-button.component";
import { UserBannerComponent } from "../../components/user-banner/user-banner.component";
import { LabeledInputComponent } from "../../../../shared/components/labeled-input/labeled-input.component";
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { UserDataService } from '../../../../core/services/user-data.service';
import { AuthValidatorService } from '../../../../core/services/validators/auth-validator.service';
import { combineLatest, firstValueFrom } from 'rxjs';
import { CompanyService } from '../../../../core/api/company.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastComponent } from '../../../../shared/components/toast/toast.component';
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { CustomFooterComponent } from "../../../../shared/components/custom-footer/custom-footer.component";
import { CustomAutocompleteComponent } from "../../../../shared/components/custom-autocomplete/custom-autocomplete.component";
import { AtecoCode } from '../../../../core/interfaces/company-interfaces';
import { LanguageService } from '../../../../core/services/language.service';

@Component({
  selector: 'app-settings-company-information-page',
  standalone: true,
  imports: [DesktopSidebarComponent, DesktopTopbarComponent, MobileTopbarComponent, TabButtonComponent, UserBannerComponent, LabeledInputComponent, TranslocoPipe, ToastComponent, CustomFooterComponent, CustomAutocompleteComponent],
  templateUrl: './settings-company-information-page.component.html',
  styleUrl: './settings-company-information-page.component.css'
})
export class SettingsCompanyInformationPageComponent implements OnInit {
  selectedSidebarItemId: number = 13;

  // Text Inputs
  companyNameInput: string = '';
  companyNameInputError: string | null = null;

  industryCodeInput: string = '';
  industryCodeInputError: string | null = null;

  userLanguage: string = 'en';

  // Ateco code
  selectedAtecoCode: AtecoCode | null = null;
  allAtecoCodes: AtecoCode[] = [];
  atecoCodesLoaded: boolean = false;

  // Edit
  editingInputDisabled: boolean = true;
  isUserEditing: boolean = false;

  // Save clicked state
  saveClicked: boolean = false;

  // Translation
  clickEditTitle = 'Click the Edit button to modify the data, then save your changes.';

  // Toast
  @ViewChild(ToastComponent) toast!: ToastComponent;

  constructor(private userData: UserDataService, private authValidator: AuthValidatorService, private companyService: CompanyService, private apiError: ApiErrorService, private translocoService: TranslocoService, private languageService: LanguageService) { }

  ngOnInit(): void {
    this.companyNameInput = this.userData.getUser()?.company?.name ?? '';
    this.industryCodeInput = this.userData.getUser()?.company?.industry_code ?? '';

    this.userLanguage = this.languageService.getCurrentUILanguage();

    combineLatest([
      this.translocoService.selectTranslate('admin.click_edit')
    ]).subscribe((
      [
        clickEditTranslate
      ]
    ) => {
      this.clickEditTitle = clickEditTranslate;
    });

    this.loadAtecoCodes();
  }

  async editSaveButtonClick() {
    if (this.saveClicked) {
      return;
    }


    this.editingInputDisabled = !this.editingInputDisabled;
    this.isUserEditing = !this.isUserEditing;

    if (this.isUserEditing) {
      return;
    }

    // Save data if user is done with editing
    this.saveClicked = true;

    // Trim inputs
    const companyName = this.companyNameInput.trim();
    const industryCode = this.selectedAtecoCode?.code ?? '';

    // Validate inputs
    const companyNameError = this.authValidator.validateCompanyName(companyName);
    const industryCodeError = (this.selectedAtecoCode == null) ? this.translocoService.translate('auth.select_ateco_code') || 'error' : null;

    this.companyNameInputError = companyNameError;
    this.industryCodeInputError = industryCodeError;

    if (companyNameError || industryCodeError) {
      this.saveClicked = false;

      this.editingInputDisabled = false;
      this.isUserEditing = true;
      return;
    }

    // Update company
    let companyResponse = undefined;

    var company = this.userData.getUser()?.company;
    if (!company) {
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage("UNKNOWN-CRID"), 'error', 8000, false);
      return;
    }

    company.name = companyName;
    company.industry_code = industryCode;

    try {
      companyResponse = await firstValueFrom(this.companyService.update(company));

      // Update user data
      var newUserData = this.userData.getUser();
      newUserData!.company = company;
      this.userData.setUser(newUserData!);

    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      this.saveClicked = false;
      this.editingInputDisabled = false;
      this.isUserEditing = true;
      return;
    }
    this.saveClicked = false;
    this.toast.showToast('shared.success', 'shared.toast.data_updated', 'success', 4000, true);
  }

  loadAtecoCodes() {
    this.companyService.getAtecoCodes().subscribe({
      next: (response) => {
        if (response) {
          this.allAtecoCodes = response;
          this.atecoCodesLoaded = true;

          this.selectedAtecoCode = this.allAtecoCodes.find(
            (code: AtecoCode) => code.code === this.industryCodeInput
          ) || null;
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      },
      complete: () => {

      }
    });
  }

  labelRendererFn = (item: any) => {
    const lang = this.userLanguage
    let description;
    if (lang === 'it'){
      description = item?.description_italian
    }
    else {
      description = item?.description_english
    }
    if (description) return `${item.code} - ${description}`;
    else return `${item.code}`;
  };
}
